//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { AccountUuid, Doc, Ref } from '@hcengineering/core'
import type { Project } from '@hcengineering/tracker'

/**
 * Kind of a gadget. Adding a value here requires extending the renderer
 * switch in `dashboard-resources/src/components/GadgetRenderer.svelte`.
 *
 * @public
 */
export type GadgetKind =
  | 'statusDistribution'
  | 'assigneeOpen'
  | 'createdVsResolved'
  | 'dueSoon'
  | 'issueCalendar'
  | 'savedFilter'
  | 'savedDocFilter'
  | 'twoDimension'

/**
 * Size preset of a gadget on the 12-column grid.
 *
 * @public
 */
export type GadgetSize = 'small' | 'medium' | 'large'

/**
 * Configuration of a single gadget. Embedded into the {@link Dashboard} document
 * rather than stored as a separate class - see docs/DSH-002-data-model.md.
 *
 * @public
 */
export interface GadgetConfig {
  /** Unique within the dashboard. Generated on creation. */
  id: string
  kind: GadgetKind
  /** Overrides the default label of the kind when set. */
  title?: string
  /** Order within the grid. Lower comes first. */
  order: number
  /**
   * Size preset. Kept as the fallback for `w`/`h`: dashboards saved before
   * free resizing existed carry only this, and reading through it means they
   * keep rendering identically with no migration.
   */
  size: GadgetSize
  /** Width in grid columns, 1-12. Falls back to the preset when unset. */
  w?: number
  /** Height in grid rows. Falls back to the preset when unset. */
  h?: number
  /** Kind specific options. Shape depends on `kind`. */
  params?: Record<string, any>
}

/**
 * A dashboard is either personal (owner set) or shared across the
 * workspace (owner === null).
 *
 * @public
 */
export interface Dashboard extends Doc {
  name: string
  description?: string
  /** `null` means the dashboard is shared with the whole workspace. */
  owner: AccountUuid | null
  /** Empty array means "every project". */
  projects: Ref<Project>[]
  /** Look-back window in days used by time based gadgets. */
  periodDays: number
  gadgets: GadgetConfig[]
}

/**
 * Widths of the grid presets, in columns of a 12 column grid.
 *
 * @public
 */
export const gadgetSizeColumns: Record<GadgetSize, number> = {
  small: 4,
  medium: 6,
  large: 12
}

/**
 * Longest period a dashboard may look back. Time based gadgets read
 * transactions, so the window is capped on purpose - see DSH-007.
 *
 * @public
 */
export const maxPeriodDays = 90

/**
 * Periods offered in the dashboard header.
 *
 * @public
 */
export const periodOptions = [7, 14, 30, 60, 90]

/**
 * Bounds for free resizing. A gadget narrower than three columns cannot fit a
 * legend beside a chart, and one shorter than three rows clips its own header.
 *
 * @public
 */
export const gridColumnCount = 12
export const minGadgetColumns = 3
export const minGadgetRows = 3
export const maxGadgetRows = 16
